import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './store/hooks';
import {
    logout,
    logoutUser,
    initializeAuth,
    selectIsAuthenticated,
    selectUser,
    selectIsInitialized,
    selectRegistrationComplete,
    selectOrganisationProvisioned,
    selectPasswordResetRequired,
} from './store/slices/authSlice';
import { UiViewKey } from './constants/uiViewKeys';

// Session management
import useSessionManager from './hooks/useSessionManager';
import InactivityWarningModal from './components/InactivityWarningModal';

// Auth pages
import Login from './components/Login';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import ForgotPassword from './components/ForgotPassword';
import NewPassword from './components/NewPassword';
import UserDetailsForm from './components/UserDetailsForm';
import OrganisationSetup from './components/OrganisationSetup';

// Main app pages
import TeamPage from './components/TeamPage';
import OrganisationPage from './components/OrganisationPage';
import ProfileWorkItemsPage from './components/ProfileWorkItemsPage';
import ProfilesPage from './components/ProfilesPage';
import UiViewProfilesPage from './components/UiViewProfilesPage';
import ServiceProfilesPage from './components/ServiceProfilesPage';

// New pages (using PageShell for now - to be replaced with full implementations)
import BookingsPage from './components/BookingsPage';
import CommunicationsPage from './components/CommunicationsPage';
import InvoicesPage from './components/InvoicesPage';
import JobsPage from './components/JobsPage';
import QuotesPage from './components/QuotesPage';
import MobileQuotesPage from './components/mobile/MobileQuotesPage';
import ResourcesPage from './components/ResourcesPage';
import StockPage from './components/StockPage';
import ProcurementPage from './components/ProcurementPage';
import VehicleServiceRecordsPage from './components/VehicleServiceRecordsPage';
import AccountingSoftwarePage from './components/AccountingSoftwarePage';
import FleetCustomersPage from './components/FleetCustomersPage';
import FleetVehiclesPage from './components/FleetVehiclesPage';
import FleetBookingsPage from './components/FleetBookingsPage';
import FleetInvoicesPage from './components/FleetInvoicesPage';
import FleetServiceRecordsPage from './components/FleetServiceRecordsPage';
import FleetSettingsPage from './components/FleetSettingsPage';

// Profile menu pages
import MyAccountPage from './components/MyAccountPage.jsx';
import HelpPage from './components/HelpPage';

// Phone build — see the mobile routing block below
import useIsMobile from './services/useIsMobile';
import MobileBookingPage from './components/mobile/MobileBookingPage';
import MobileAccountPage from './components/mobile/MobileAccountPage';
import MobileNotificationsPage from './components/mobile/MobileNotificationsPage';
import MobileShell from './components/mobile/MobileShell';
import MobileFleetBookingsPage from './components/mobile/MobileFleetBookingsPage';
import MobileFleetInvoicesPage from './components/mobile/MobileFleetInvoicesPage';
import MobileInvoicesPage from './components/mobile/MobileInvoicesPage';


// ============================================
// Loading Spinner
// ============================================

const LoadingSpinner = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading...</p>
        </div>
    </div>
);

// ============================================
// Landing route resolution
// ============================================

/**
 * Ordered list of (UiViewKey, path) pairs used to pick where a user lands
 * after login, and where to bounce them when they hit a page their
 * uiViews don't include.
 *
 * Order matters — the first entry the user has access to wins. Bookings
 * stays first so that the common case (users with full access, which
 * includes OWNERs) still lands there. A job-only user falls through to
 * /jobs, a resource planner to /resources, and so on.
 *
 * Profile-menu routes are intentionally omitted: /profile, /security,
 * /settings, /help are always reachable via the avatar menu and don't
 * make sense as a default landing destination.
 */
const LANDING_CANDIDATES = [
    { key: UiViewKey.BOOKINGS,                   path: '/bookings' },
    { key: UiViewKey.JOBS,                       path: '/jobs' },
    { key: UiViewKey.CUSTOMER_COMMUNICATIONS,    path: '/communications' },
    { key: UiViewKey.INVOICES,                   path: '/invoices' },
    { key: UiViewKey.SERVICE_RECORDS,            path: '/service-records' },
    { key: UiViewKey.FLEET_CUSTOMERS,            path: '/fleet-customers' },

    // Fleet portal — a CLIENT holds none of the workshop keys above, so these
    // are the only candidates that will match for them. My Vehicles is first,
    // making it the fleet customer's landing page after login.
    { key: UiViewKey.FLEET_VEHICLES,             path: '/fleet/vehicles' },
    { key: UiViewKey.FLEET_BOOKINGS,             path: '/fleet/bookings' },
    { key: UiViewKey.FLEET_INVOICES,             path: '/fleet/invoices' },
    { key: UiViewKey.FLEET_SERVICE_RECORDS,      path: '/fleet/service-records' },
    { key: UiViewKey.FLEET_SETTINGS,             path: '/fleet/settings' },
    { key: UiViewKey.RESOURCE_PLANNING,          path: '/resources' },
    { key: UiViewKey.STOCK_MANAGEMENT,           path: '/stock' },
    { key: UiViewKey.PROCUREMENT,                path: '/procurement' },
    { key: UiViewKey.WORK_ITEMS,                 path: '/work-item-profiles' },
    { key: UiViewKey.SERVICE_PROFILES,           path: '/service-profiles' },
    { key: UiViewKey.TEAM_MEMBERS,               path: '/team' },
    { key: UiViewKey.STAFF_PERMISSION_PROFILES,  path: '/profiles' },
    { key: UiViewKey.UI_VIEW_PROFILES,           path: '/ui-view-profiles' },
    { key: UiViewKey.ORGANISATION,               path: '/organisation' },
];

// Fallback destination if a user somehow has zero view access.
// /profile is always navigable via the avatar menu, so it's a safe
// "you're logged in, here's a page that exists" target.
const FALLBACK_LANDING = '/account';

/**
 * Pick the first landing candidate the user actually has access to.
 * Returns FALLBACK_LANDING when uiViews is empty or contains only
 * profile-menu entries.
 */
function pickLandingForUiViews(uiViews) {
    const allowed = new Set(uiViews || []);
    const match = LANDING_CANDIDATES.find(c => allowed.has(c.key));
    return match ? match.path : FALLBACK_LANDING;
}

// ============================================
// Phone build
// ============================================

/**
 * Views that have a phone screen. Holding one of these is what earns a session the
 * phone shell instead of the desktop layout.
 *
 * <p>The phone build deliberately carries a subset — the remaining pages are dense
 * multi-column screens a 375px viewport cannot show honestly. Workshop staff get
 * Jobs and Bookings, which is what the floor needs. Fleet customers get their
 * bookings and invoices; My Vehicles and Service History stay on the
 * desktop layout for now, reachable by URL on a phone exactly as before.
 *
 * <p>⚠️ This list must stay in step with MobileShell's TABS. A view here with no tab
 * gives a session the shell and then no way to navigate; a tab whose view is not
 * here never gets the shell to render in.
 */
const MOBILE_VIEWS = [
    UiViewKey.JOBS,
    UiViewKey.BOOKINGS,
    UiViewKey.QUOTES,
    UiViewKey.INVOICES,
    UiViewKey.FLEET_BOOKINGS,
    UiViewKey.FLEET_INVOICES,
];

/**
 * Whether this session gets the phone app shell instead of the desktop one.
 *
 * <p>Scoped to users who actually hold a view the phone build carries. A session
 * holding none of them keeps the responsive desktop routes on a phone — routing it
 * into a tab shell with no tabs it can see would leave it with a dead app.
 */
function usesMobileShell(isMobile, uiViews) {
    if (!isMobile) return false;
    const allowed = new Set(uiViews || []);
    return MOBILE_VIEWS.some((v) => allowed.has(v));
}

/**
 * Landing route for the phone shell, in priority order.
 *
 * Jobs first — the mechanic on the floor is the primary phone user; a bookings-only
 * user (front counter) falls through to /bookings. A fleet customer holds neither
 * and lands on their own bookings, which is what they opened the app to see. All
 * four missing is impossible here, since usesMobileShell already required one.
 */
function pickMobileLanding(uiViews) {
    const allowed = new Set(uiViews || []);
    if (allowed.has(UiViewKey.JOBS))            return '/jobs';
    if (allowed.has(UiViewKey.BOOKINGS))        return '/bookings';
    if (allowed.has(UiViewKey.FLEET_BOOKINGS))  return '/fleet/bookings';
    if (allowed.has(UiViewKey.FLEET_INVOICES))  return '/fleet/invoices';
    return '/me';
}

// ============================================
// Route Guard Helper
// ============================================

/**
 * Determines the correct route based on the user's registration progress.
 * Flow: Login → (Reset Password if required) → Complete Profile → Setup Organisation → first allowed view
 *
 * passwordResetRequired takes precedence over every other post-login step —
 * the restricted token can't actually call any of the downstream APIs.
 *
 * Once fully provisioned, the landing page is derived from the user's
 * uiViews claim so a user without BOOKINGS access doesn't get parked on
 * /bookings (which would trigger 403 storms on BookingsPage's data loads).
 */
function getDefaultRoute(
    isAuthenticated,
    registrationComplete,
    organisationProvisioned,
    passwordResetRequired,
    uiViews,
    mobileShell = false
) {
    if (!isAuthenticated) return '/login';
    if (passwordResetRequired) return '/reset-password';
    if (!registrationComplete) return '/complete-profile';
    if (!organisationProvisioned) return '/setup-organisation';
    // The desktop landing list includes routes the phone build doesn't mount
    // (/invoices, /service-records, …). Landing a phone user there would hit
    // the mobile catch-all and bounce, so the phone picks from its own tabs.
    return mobileShell ? pickMobileLanding(uiViews) : pickLandingForUiViews(uiViews);
}

// ============================================
// Session shell
// ============================================

/**
 * Wraps the protected children with the session manager + inactivity
 * warning modal. Mounted exactly once at the protected boundary so the
 * activity tracker, idle timer, and proactive refresh run for every
 * authenticated page but never on /login, /register, /reset-password.
 *
 * Kept separate from <ProtectedRoute> so the hook doesn't get invoked
 * on early returns (e.g. when ProtectedRoute decides to redirect to
 * /complete-profile, no timers should start).
 */
const SessionShell = ({ children }) => {
    const {
        warningOpen,
        secondsLeft,
        dismissWarning,
        signOutNow,
    } = useSessionManager();

    return (
        <>
            {children}
            <InactivityWarningModal
                open={warningOpen}
                secondsLeft={secondsLeft}
                onStaySignedIn={dismissWarning}
                onSignOutNow={signOutNow}
            />
        </>
    );
};

/**
 * A protected route wrapper that redirects based on auth + registration state.
 *
 * If passwordResetRequired is set, the user is bounced to /reset-password
 * regardless of the target route. Their restricted token can't authorise
 * any real endpoint, so mounting a normal page just produces a storm of 403s.
 *
 * If `requireView` is supplied, the user's uiViews claim must include that
 * key. Without it they're bounced to whatever landing page they DO have
 * access to. This prevents direct URL entry (or stale links) from mounting
 * a page whose data-load endpoints will 403.
 *
 * On the happy path, children render inside <SessionShell> which hosts
 * the inactivity timer + proactive token refresh.
 */
const ProtectedRoute = ({
                            children,
                            requireRegistration = false,
                            requireOrganisation = false,
                            requireView = null,
                        }) => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const registrationComplete = useAppSelector(selectRegistrationComplete);
    const organisationProvisioned = useAppSelector(selectOrganisationProvisioned);
    const passwordResetRequired = useAppSelector(selectPasswordResetRequired);
    const user = useAppSelector(selectUser);
    const isMobile = useIsMobile();

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Forced reset trumps everything else
    if (passwordResetRequired) {
        return <Navigate to="/reset-password" replace />;
    }

    // If route requires registration to be complete but it isn't
    if (requireRegistration && !registrationComplete) {
        return <Navigate to="/complete-profile" replace />;
    }

    // If route requires org provisioning but it isn't done
    if (requireOrganisation && !organisationProvisioned) {
        if (!registrationComplete) {
            return <Navigate to="/complete-profile" replace />;
        }
        return <Navigate to="/setup-organisation" replace />;
    }

    // UI view gating. A missing requireView means "any authenticated user
    // who's made it this far", which we use for profile-menu pages.
    if (requireView) {
        const allowed = new Set(user?.uiViews || []);
        if (!allowed.has(requireView)) {
            // Bounce to a route this device actually mounts. Using the desktop
            // list on a phone would send the user to e.g. /invoices, which the
            // phone build doesn't declare — the catch-all would then redirect
            // again, producing a visible double hop.
            const landing = usesMobileShell(isMobile, user?.uiViews)
                ? pickMobileLanding(user?.uiViews)
                : pickLandingForUiViews(user?.uiViews);
            return <Navigate to={landing} replace />;
        }
    }

    return <SessionShell>{children}</SessionShell>;
};

// ============================================
// App Component
// ============================================

function App() {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const isInitialized = useAppSelector(selectIsInitialized);
    const registrationComplete = useAppSelector(selectRegistrationComplete);
    const organisationProvisioned = useAppSelector(selectOrganisationProvisioned);
    const passwordResetRequired = useAppSelector(selectPasswordResetRequired);
    const user = useAppSelector(selectUser);
    const isMobile = useIsMobile();

    // Initialize auth on app load
    useEffect(() => {
        dispatch(initializeAuth());
    }, [dispatch]);

    // Show loading spinner only while initializing auth on first load
    if (!isInitialized) {
        return <LoadingSpinner />;
    }

    // Rotating a phone to landscape, or resizing a desktop window past 768px,
    // swaps the entire protected route set. That is intentional: the two sets
    // share their paths (/jobs, /bookings), so the URL survives the swap and the
    // user stays on the screen they were looking at.
    const mobileShell = usesMobileShell(isMobile, user?.uiViews);

    const defaultRoute = getDefaultRoute(
        isAuthenticated,
        registrationComplete,
        organisationProvisioned,
        passwordResetRequired,
        user?.uiViews,
        mobileShell
    );

    return (
        <Router basename={import.meta.env.VITE_BASE_URL || "/wadakaroo"}>
            <Routes>
                {/* ============================================ */}
                {/* Public Routes                                */}
                {/* ============================================ */}

                <Route
                    path="/login"
                    element={
                        isAuthenticated ? (
                            <Navigate to={defaultRoute} replace />
                        ) : (
                            <Login />
                        )
                    }
                />

                <Route
                    path="/register"
                    element={
                        isAuthenticated ? (
                            <Navigate to={defaultRoute} replace />
                        ) : (
                            <Register />
                        )
                    }
                />

                {/* Forgot password (logged-out): request a reset link */}
                <Route
                    path="/forgot-password"
                    element={
                        isAuthenticated ? (
                            <Navigate to={defaultRoute} replace />
                        ) : (
                            <ForgotPassword />
                        )
                    }
                />

                {/*
                 * Destination of the reset-link email. Always rendered — the token
                 * in the URL is the credential, so it must work even if a stale
                 * session exists in this browser.
                 */}
                <Route path="/new-password" element={<NewPassword />} />

                {/* ============================================ */}
                {/* Forced Password Reset                        */}
                {/* ============================================ */}

                {/*
                 * The user IS authenticated here, but only holds a restricted
                 * PASSWORD_RESET_REQUIRED token. This route is the single
                 * destination that token can reach until the reset succeeds.
                 */}
                <Route
                    path="/reset-password"
                    element={
                        !isAuthenticated ? (
                            <Navigate to="/login" replace />
                        ) : !passwordResetRequired ? (
                            <Navigate to={defaultRoute} replace />
                        ) : (
                            <ResetPassword />
                        )
                    }
                />

                {/* ============================================ */}
                {/* Registration Flow Routes                     */}
                {/* ============================================ */}

                {/* Step 2: Complete user profile details */}
                <Route
                    path="/complete-profile"
                    element={
                        !isAuthenticated ? (
                            <Navigate to="/login" replace />
                        ) : passwordResetRequired ? (
                            <Navigate to="/reset-password" replace />
                        ) : registrationComplete ? (
                            <Navigate to={defaultRoute} replace />
                        ) : (
                            <UserDetailsForm />
                        )
                    }
                />

                {/* Step 3: Setup organisation */}
                <Route
                    path="/setup-organisation"
                    element={
                        !isAuthenticated ? (
                            <Navigate to="/login" replace />
                        ) : passwordResetRequired ? (
                            <Navigate to="/reset-password" replace />
                        ) : !registrationComplete ? (
                            <Navigate to="/complete-profile" replace />
                        ) : organisationProvisioned ? (
                            <Navigate to={defaultRoute} replace />
                        ) : (
                            <OrganisationSetup />
                        )
                    }
                />

                {/* ============================================ */}
                {/* Protected Routes — PHONE build               */}
                {/* ============================================ */}
                {/* Jobs, Bookings and the Me tab, and nothing    */}
                {/* else. Every other path — including deep links */}
                {/* and bookmarks into pages that only exist on   */}
                {/* desktop — falls through to the catch-all and  */}
                {/* lands on a tab the user can actually see.     */}
                {/*                                               */}
                {/* /jobs is the SAME JobsPage as desktop: it      */}
                {/* already branches to MobileJobsDashboard        */}
                {/* internally and scopes the fetch to the         */}
                {/* signed-in tech's own jobs (myJobsOnly).        */}

                {mobileShell && (
                    <>
                        <Route
                            path="/jobs"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.JOBS}>
                                    <JobsPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/bookings"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.BOOKINGS}>
                                    <MobileShell>
                                        <MobileBookingPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/quotes"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.QUOTES}>
                                    <MobileShell>
                                        <MobileQuotesPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/invoices"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.INVOICES}>
                                    <MobileShell>
                                        <MobileInvoicesPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        {/* ── Fleet portal on a phone (CLIENT users) ──────────
                            Same paths as the desktop fleet routes, so rotating a
                            phone to landscape swaps the route set without moving
                            the user off the screen they were on. */}
                        <Route
                            path="/fleet/bookings"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_BOOKINGS}>
                                    <MobileShell>
                                        <MobileFleetBookingsPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/fleet/invoices"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_INVOICES}>
                                    <MobileShell>
                                        <MobileFleetInvoicesPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        {/* Shell chrome, not a product view — no requireView, for the
                            same reason /account has none on desktop. Notifications are
                            addressed to a staff member rather than granted by a view
                            key, so the tab is hidden for a session with no staff
                            identity instead of being gated here. */}
                        <Route
                            path="/notifications"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation>
                                    <MobileShell>
                                        <MobileNotificationsPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/me"
                            element={
                                <ProtectedRoute requireRegistration requireOrganisation>
                                    <MobileShell>
                                        <MobileAccountPage />
                                    </MobileShell>
                                </ProtectedRoute>
                            }
                        />

                        <Route path="/" element={<Navigate to={defaultRoute} replace />} />
                        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
                    </>
                )}

                {/* ============================================ */}
                {/* Protected Routes - Operations                */}
                {/* ============================================ */}

                {!mobileShell && (
                <>
                <Route
                    path="/bookings"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.BOOKINGS}>
                            <BookingsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/communications"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.CUSTOMER_COMMUNICATIONS}>
                            <CommunicationsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/invoices"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.INVOICES}>
                            <InvoicesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/quotes"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.QUOTES}>
                            <QuotesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/jobs"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.JOBS}>
                            <JobsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/service-records"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.SERVICE_RECORDS}>
                            <VehicleServiceRecordsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/fleet-customers"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_CUSTOMERS}>
                            <FleetCustomersPage />
                        </ProtectedRoute>
                    }
                />

                {/* ── Fleet portal (CLIENT users) ───────────────────────────── */}

                <Route
                    path="/fleet/vehicles"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_VEHICLES}>
                            <FleetVehiclesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/fleet/bookings"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_BOOKINGS}>
                            <FleetBookingsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/fleet/invoices"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_INVOICES}>
                            <FleetInvoicesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/fleet/service-records"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_SERVICE_RECORDS}>
                            <FleetServiceRecordsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/fleet/settings"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.FLEET_SETTINGS}>
                            <FleetSettingsPage />
                        </ProtectedRoute>
                    }
                />

                

                {/* ============================================ */}
                {/* Protected Routes - Resources & Inventory     */}
                {/* ============================================ */}

                <Route
                    path="/resources"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.RESOURCE_PLANNING}>
                            <ResourcesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/stock"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.STOCK_MANAGEMENT}>
                            <StockPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/procurement"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.PROCUREMENT}>
                            <ProcurementPage />
                        </ProtectedRoute>
                    }
                />

                

                {/* ============================================ */}
                {/* Protected Routes - Configuration             */}
                {/* ============================================ */}

                <Route
                    path="/work-item-profiles"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.WORK_ITEMS}>
                            <ProfileWorkItemsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/service-profiles"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.SERVICE_PROFILES}>
                            <ServiceProfilesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/team"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.TEAM_MEMBERS}>
                            <TeamPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/profiles"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.STAFF_PERMISSION_PROFILES}>
                            <ProfilesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/ui-view-profiles"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.UI_VIEW_PROFILES}>
                            <UiViewProfilesPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/accounting-software"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.ACCOUNTING_SOFTWARE}>
                            <AccountingSoftwarePage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/organisation"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation requireView={UiViewKey.ORGANISATION}>
                            <OrganisationPage />
                        </ProtectedRoute>
                    }
                />

                {/* ============================================ */}
                {/* Profile menu pages                           */}
                {/* ============================================ */}
                {/* No requireView on these — the avatar menu     */}
                {/* gates visibility of individual entries via     */}
                {/* uiViews, but the routes themselves remain      */}
                {/* reachable to any authenticated user. This is   */}
                {/* deliberate: it's the natural "fallback home"   */}
                {/* for users with no operational views.           */}

                <Route
                    path="/account"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation>
                            <MyAccountPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/help"
                    element={
                        <ProtectedRoute requireRegistration requireOrganisation>
                            <HelpPage />
                        </ProtectedRoute>
                    }
                />

                {/* /me only exists in the phone build. Widening the viewport
                    past 768px — rotating a foldable, resizing a browser window,
                    casting to a screen — swaps the route set out from under
                    whoever is standing on it, and without this they land on the
                    404 card. Redirect instead, so the transition is invisible. */}
                <Route path="/me" element={<Navigate to={defaultRoute} replace />} />

                {/* ============================================ */}
                {/* Default & 404 Routes                         */}
                {/* ============================================ */}

                <Route
                    path="/"
                    element={
                        <Navigate to={isAuthenticated ? defaultRoute : '/login'} replace />
                    }
                />

                <Route
                    path="*"
                    element={
                        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                            <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center border border-gray-200">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-3xl">🔍</span>
                                </div>
                                <h2 className="text-xl font-display font-bold text-gray-900 mb-2">Page not found</h2>
                                <p className="text-gray-500 mb-6 text-sm">
                                    The page you're looking for doesn't exist or has been moved.
                                </p>
                                <Link
                                    to={isAuthenticated ? defaultRoute : '/login'}
                                    className="inline-block px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold"
                                >
                                    {isAuthenticated ? 'Take me home' : 'Sign in'}
                                </Link>
                            </div>
                        </div>
                    }
                />
                </>
                )}
            </Routes>
        </Router>
    );
}

export default App;